import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { UXState, Persona, Axes, Theme, Locale, PERSONA_PRESETS } from './types'
import {
  parseQueryParams,
  loadFromLocalStorage,
  saveToLocalStorage,
  updateQueryParams,
} from './query'

interface UXContextType extends UXState {
  setPersona: (persona: Persona) => void
  setAxis: (axis: keyof Axes, value: number) => void
  setDemo: (demo: boolean) => void
  setTheme: (theme: Theme) => void
  setLocale: (locale: Locale) => void
  resetToPreset: () => void
}

const UXContext = createContext<UXContextType | null>(null)

function detectReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function detectSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getInitialState(): UXState {
  // Priority: Query params > LocalStorage > Defaults
  const queryState = parseQueryParams()
  const storedState = loadFromLocalStorage()

  const persona = queryState.persona || storedState.persona || 'recruiter'
  const demo = queryState.demo || storedState.demo || false
  const theme = queryState.theme || storedState.theme || 'system'
  const locale = queryState.locale || storedState.locale || 'en' // Default EN as requested

  let axes = queryState.axes || storedState.axes || PERSONA_PRESETS[persona]

  // Auto-detect reduced motion
  if (detectReducedMotion()) {
    axes = { ...axes, motion: 0 }
  }

  return { persona, axes, demo, theme, locale }
}

export function UXProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UXState>(getInitialState)

  useEffect(() => {
    // Sync to localStorage and query params
    saveToLocalStorage(state)
    updateQueryParams(state)
  }, [state])

  // Apply dark mode class to document
  useEffect(() => {
    const effectiveTheme = state.theme === 'system' ? detectSystemTheme() : state.theme

    if (effectiveTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (state.theme === 'system') {
        const newTheme = detectSystemTheme()
        document.documentElement.classList.toggle('dark', newTheme === 'dark')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [state.theme])

  const setPersona = (persona: Persona) => {
    setState((prev) => ({
      ...prev,
      persona,
      axes: PERSONA_PRESETS[persona], // Reset to preset
    }))
  }

  const setAxis = (axis: keyof Axes, value: number) => {
    setState((prev) => ({
      ...prev,
      axes: { ...prev.axes, [axis]: Math.max(0, Math.min(1, value)) },
    }))
  }

  const setDemo = (demo: boolean) => {
    setState((prev) => ({ ...prev, demo }))
  }

  const setTheme = (theme: Theme) => {
    setState((prev) => ({ ...prev, theme }))
  }

  const setLocale = (locale: Locale) => {
    setState((prev) => ({ ...prev, locale }))
    // Update html lang attribute for accessibility
    document.documentElement.lang = locale
  }

  const resetToPreset = () => {
    setState((prev) => ({
      ...prev,
      axes: PERSONA_PRESETS[prev.persona],
    }))
  }

  // Set initial lang attribute
  useEffect(() => {
    document.documentElement.lang = state.locale
  }, [state.locale])

  const contextValue: UXContextType = {
    ...state,
    setPersona,
    setAxis,
    setDemo,
    setTheme,
    setLocale,
    resetToPreset,
  }

  return <UXContext.Provider value={contextValue}>{children}</UXContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUX() {
  const context = useContext(UXContext)
  if (!context) throw new Error('useUX must be used within UXProvider')
  return context
}
